import { ButtonHint } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PillarScopeType } from '@/constants/PillarScope';
import type { FilteredTeam } from '@/hooks/objectives/useObjectiveListLens';

import FilteredTeamModal from '@/components/objectives/FilteredTeamModal';
import { PillarScope } from '@/constants/PillarScope';

interface PillarScopeMentionProps {
    /** The scope in effect, which the sentence names. */
    scope: PillarScopeType;
    /** What the sentence speaks of: the row of pillars, or the pillar of its page. */
    subject: 'pillars' | 'pillar';
    /**
     * The people of the reader's team the filters keep: under "selected filters", the sentence
     * counts them and the count opens their list. Absent for a reader who sees the whole company,
     * who reads the sentence bare.
     */
    filteredTeam?: FilteredTeam;
}

const MENTION_KEYS = {
    pillars: {
        organization: 'objectives.pillarScope.pillarsMention.organization',
        filters: 'objectives.pillarScope.pillarsMention.filters',
        filtersForTeam: 'objectives.pillarScope.pillarsMention.filtersForTeam',
        team: 'objectives.pillarScope.pillarsMention.team',
    },
    pillar: {
        organization: 'objectives.pillarScope.pillarMention.organization',
        filters: 'objectives.pillarScope.pillarMention.filters',
        filtersForTeam: 'objectives.pillarScope.pillarMention.filtersForTeam',
        team: 'objectives.pillarScope.pillarMention.team',
    },
} as const;

/**
 * The sentence under the pillar scope saying whose objectives the pillars are counted among. Under
 * "selected filters", for a reader whose reach is their team, it counts the people the filters keep
 * and opens their list.
 */
export default function PillarScopeMention({
    scope,
    subject,
    filteredTeam,
}: PillarScopeMentionProps) {
    const { t } = useTranslation();
    const [isTeamListOpen, setIsTeamListOpen] = useState(false);

    const mentionKeys = MENTION_KEYS[subject];
    // Until the filters resolve, a count would read as zero: the sentence waits for it bare.
    const countedTeam =
        scope === PillarScope.FILTERS && filteredTeam && !filteredTeam.isResolving
            ? filteredTeam.employees
            : undefined;

    if (!countedTeam) {
        return <p>{t(mentionKeys[scope])}</p>;
    }

    const count = countedTeam.length;
    const countLabel = t('objectives.pillarScope.filteredTeam.count', { count });

    return (
        <>
            <p>
                {t(mentionKeys.filtersForTeam, { count })}{' '}
                {count === 0 ? (
                    countLabel
                ) : (
                    <ButtonHint
                        onClick={() => setIsTeamListOpen(true)}
                        ariaLabel={t('objectives.pillarScope.filteredTeam.openList', { count })}
                        aria-haspopup={'dialog'}
                        dataTestId={'pillar-scope-filtered-team'}
                    >
                        {countLabel}
                    </ButtonHint>
                )}
            </p>

            <FilteredTeamModal
                isOpen={isTeamListOpen && count > 0}
                onClose={() => setIsTeamListOpen(false)}
                employees={countedTeam}
            />
        </>
    );
}
